import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Search, AlertCircle, ArrowRight, ExternalLink, Sparkles, Download, Image as ImageIcon, ChevronDown, ChevronUp, Star, Crop } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import { toast } from "sonner";

const PLACEHOLDER_KEYS = [
  "hair_description",
  "skin_tone",
  "industry",
  "wardrobe_notes",
  "preferred_settings",
];

const CATEGORIES = [
  "All",
  "Professional Headshots",
  "Lifestyle & Personal Brand",
  "Industry Specific",
  "Social Media Content",
  "Seasonal & Holiday",
  "Creative & Artistic",
  "Team & Client",
  "Website & Marketing",
  "Bonus: Professional Bio",
  "Bonus: Virtual Try-On",
];

const buildProfileMap = (profile) => {
  if (!profile) return {};
  const industry = profile.industry === "Other" ? (profile.industry_other || "") : (profile.industry || "");
  return {
    hair_description: profile.hair_description || "",
    skin_tone: profile.skin_tone || "",
    industry,
    wardrobe_notes: profile.wardrobe_notes || "",
    preferred_settings: profile.preferred_settings || "",
  };
};

const personalise = (text, map) => {
  return text.replace(/\{\{(\w+)\}\}/g, (full, key) => {
    const v = map[key];
    return v && v.trim() ? v : full;
  });
};

const missingFieldsFor = (text, map) => {
  const used = new Set();
  text.replace(/\{\{(\w+)\}\}/g, (_, k) => {
    used.add(k);
    return _;
  });
  return [...used].filter((k) => !(map[k] && map[k].trim()));
};

const FRAMING_OPTIONS = [
  { key: "none", label: "Default", short: "Default", suffix: "" },
  { key: "close", label: "Close-up", short: "Close-up", suffix: "Frame this as a close-up portrait — head and shoulders only, tight crop, face fills most of the frame." },
  { key: "half", label: "Half-body", short: "Half-body", suffix: "Frame this as a half-body shot — head to waist, with some background visible." },
  { key: "three-quarter", label: "Three-quarter", short: "¾ length", suffix: "Frame this as a three-quarter length shot — head to knees, showing most of the body and outfit." },
  { key: "full", label: "Full-body", short: "Full-body", suffix: "Frame this as a full-body shot — head to feet, showing the complete outfit, posture, and full scene. Leave space above and below the person." },
  { key: "wide", label: "Wide / Environmental", short: "Wide", suffix: "Frame this as a wide environmental portrait — full body with generous space around the person, showing the complete setting and scene. Landscape orientation." },
];

const TEXT_ONLY_TITLES = new Set(["Your AI-Written Professional Bio"]);

export default function PromptLibrary() {
  const [prompts, setPrompts] = useState([]);
  const [copiedIds, setCopiedIds] = useState([]);
  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [pRes, profRes, photoRes] = await Promise.all([
      api.get("/prompts"),
      api.get("/brand-profile"),
      api.get("/reference-photos").catch(() => ({ data: { photos: [] } })),
    ]);
    setPrompts(pRes.data.prompts || []);
    setCopiedIds(pRes.data.copied_ids || []);
    setProfile(profRes.data.profile || null);
    setPhotos(photoRes.data.photos || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const profileMap = useMemo(() => buildProfileMap(profile), [profile]);

  const profileIncomplete = !profile || PLACEHOLDER_KEYS.some((k) => !(profileMap[k] && profileMap[k].trim()));

  const filtered = useMemo(() => {
    return prompts.filter((p) => {
      if (activeCat !== "All" && p.category !== activeCat) return false;
      if (search) {
        const blob = `${p.title} ${p.category} ${p.template_text}`.toLowerCase();
        if (!blob.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [prompts, activeCat, search]);

  const onCopy = async (p, framingSuffix = "") => {
    let personalisedText = personalise(p.template_text, profileMap);
    if (framingSuffix) {
      personalisedText = `${personalisedText.trimEnd()}\n\n${framingSuffix}`;
    }
    try {
      await navigator.clipboard.writeText(personalisedText);
      toast.success("Prompt copied! Now paste it into ChatGPT along with your reference photo.");
      try {
        await api.post(`/prompts/${p.id}/copied`);
      } catch (err) {
        console.warn("Could not track prompt copy:", err);
      }
      setCopiedIds((prev) => (prev.includes(p.id) ? prev : [...prev, p.id]));
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="step3-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="hairline" />
        <span className="eyebrow">Step 3 of 6 · Prompt library</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D]">Step 3: Your Ready-to-Use Prompts</h1>
      <p className="text-[#52525b] mt-3 text-lg max-w-3xl leading-relaxed">
        These prompts are personalised to your brand. Copy one, paste it into ChatGPT along with your reference photo, and watch the magic happen.
      </p>

      {/* Reference photos quick-access */}
      <ReferencePhotosPanel photos={photos} />

      {profileIncomplete && (
        <div className="mt-6 bsp-card p-5 border-l-4 border-[#D4A853] flex items-start gap-3" data-testid="profile-warning">
          <AlertCircle className="w-5 h-5 text-[#D4A853] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-[#1A1F3D]">Your prompts aren't fully personalised yet</div>
            <div className="text-sm text-[#52525b] mt-1">
              Complete your <Link to="/app/step2" className="gold-link">Brand Profile (Step 2)</Link> to fill in the placeholders below automatically.
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="relative md:max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
          <input
            placeholder="Search prompts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bsp-input pl-9"
            data-testid="prompt-search"
          />
        </div>
        <div className="text-sm text-[#52525b]">
          {filtered.length} of {prompts.length} prompts · <span className="text-[#1A1F3D] font-medium">{copiedIds.length} copied</span>
        </div>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto scrollbar-clean -mx-1 px-1 pb-1" data-testid="category-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCat(c)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
              activeCat === c
                ? "bg-[#1A1F3D] text-white"
                : "bg-white border border-[#e5e7eb] text-[#52525b] hover:border-[#D4A853]"
            }`}
            data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Cards */}
      {loading ? (
        <div className="mt-10 text-[#52525b] text-sm">Loading prompts…</div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-2 gap-5" data-testid="prompt-grid">
          {filtered.map((p) => (
            <PromptCard
              key={p.id}
              prompt={p}
              profileMap={profileMap}
              copied={copiedIds.includes(p.id)}
              onCopy={onCopy}
            />
          ))}
          {filtered.length === 0 && (
            <div className="text-[#52525b] text-sm">No prompts match that search.</div>
          )}
        </div>
      )}

      {/* What to do next */}
      <div className="mt-12 bsp-card p-7 md:p-9 bg-[#1A1F3D] text-white" data-testid="next-step-card">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4A853]">What to do next</span>
        <h2 className="font-display text-3xl mt-2">Step 4: Download your best reference photo → Open ChatGPT → Paste your prompt → Attach the photo → Generate!</h2>
        <p className="text-white/70 mt-3 max-w-2xl">
          Use the <span className="text-[#D4A853] font-medium">"Your reference photos"</span> panel above to download your best shot to your device, then bring it (and your copied prompt) into ChatGPT. Upload the results back here in Step 5.
        </p>
        <Link
          to="/app/step4"
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#D4A853] text-[#1A1F3D] font-semibold transition-colors hover:bg-[#C29543]"
          data-testid="see-instructions-link"
        >
          See the full instructions <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

const PromptCard = ({ prompt, profileMap, copied, onCopy }) => {
  const personalised = personalise(prompt.template_text, profileMap);
  const missing = missingFieldsFor(prompt.template_text, profileMap);
  const isBonus = (prompt.category || "").toLowerCase().startsWith("bonus");
  const isTextOnly = TEXT_ONLY_TITLES.has(prompt.title);
  const [framingKey, setFramingKey] = useState("none");
  const framing = FRAMING_OPTIONS.find((f) => f.key === framingKey) || FRAMING_OPTIONS[0];

  // Highlight remaining placeholders (when profile data is missing) in soft blush
  const renderText = () => {
    const parts = personalised.split(/(\{\{\w+\}\})/g);
    return parts.map((part, i) => {
      const m = part.match(/^\{\{(\w+)\}\}$/);
      if (m) {
        return (
          <span key={i} className="bg-[#F5E6E0] text-[#1A1F3D] px-1.5 rounded">
            [{m[1].replace(/_/g, " ")}]
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="bsp-card bsp-card-hover p-6 flex flex-col" data-testid={`prompt-card-${prompt.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider font-semibold text-[#D4A853]">{prompt.category}</span>
          {isBonus && (
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold bg-[#D4A853] text-[#1A1F3D] px-1.5 py-0.5 rounded" data-testid={`bonus-badge-${prompt.id}`}>
              Bonus
            </span>
          )}
        </div>
        {copied && (
          <span className="text-xs font-semibold text-[#1A1F3D] inline-flex items-center gap-1.5 bg-[#F5E6E0] px-2.5 py-1 rounded-full" data-testid={`copied-indicator-${prompt.id}`}>
            <Check className="w-3.5 h-3.5 text-[#D4A853]" strokeWidth={2.6} /> Copied
          </span>
        )}
      </div>
      <h3 className="font-display text-2xl text-[#1A1F3D] mt-2">{prompt.title}</h3>

      {missing.length > 0 && (
        <div className="mt-3 text-xs text-[#52525b] flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-[#D4A853] mt-0.5 shrink-0" />
          <span>
            Complete your <Link to="/app/step2" className="gold-link">Brand Profile (Step 2)</Link> to personalise this prompt.
          </span>
        </div>
      )}

      <p className="mt-4 text-sm text-[#2D2D2D] leading-relaxed flex-1">{renderText()}</p>

      {prompt.usage_tip && (
        <div className="mt-4 rounded-lg bg-[#F5E6E0] border-l-4 border-[#D4A853] p-3.5 flex items-start gap-2.5" data-testid={`usage-tip-${prompt.id}`}>
          <Sparkles className="w-4 h-4 text-[#D4A853] mt-0.5 shrink-0" />
          <span className="text-xs text-[#1A1F3D] leading-relaxed">{prompt.usage_tip}</span>
        </div>
      )}

      {!isTextOnly && (
        <div className="mt-5 pt-4 border-t border-[#e5e7eb]" data-testid={`framing-${prompt.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Crop className="w-3.5 h-3.5 text-[#D4A853]" strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[#52525b]">Framing</span>
            {framing.key !== "none" && (
              <span className="text-[10px] text-[#1A1F3D] bg-[#F5E6E0] px-1.5 py-0.5 rounded font-medium">
                Will append framing instruction
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Framing">
            {FRAMING_OPTIONS.map((opt) => {
              const active = framingKey === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setFramingKey(opt.key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-[#1A1F3D] text-white border-[#1A1F3D]"
                      : "bg-white text-[#52525b] border-[#e5e7eb] hover:border-[#D4A853] hover:text-[#1A1F3D]"
                  }`}
                  data-testid={`framing-${prompt.id}-${opt.key}`}
                  title={opt.suffix || "No framing override — use the prompt's default"}
                >
                  {opt.short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={`${isTextOnly ? "mt-5 pt-4 border-t border-[#e5e7eb]" : "mt-4"} flex items-center justify-between gap-3`}>
        <span className="text-xs text-[#52525b] inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#D4A853]" />
          {missing.length === 0 ? "Personalised for you" : `${missing.length} placeholder${missing.length === 1 ? "" : "s"} unfilled`}
        </span>
        <button
          onClick={() => onCopy(prompt, framing.suffix)}
          className={`px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center gap-2 transition-colors ${
            copied
              ? "bg-[#F5E6E0] text-[#1A1F3D] hover:bg-[#ecd6cd]"
              : "bg-[#D4A853] text-[#1A1F3D] hover:bg-[#C29543]"
          }`}
          data-testid={`copy-btn-${prompt.id}`}
        >
          <Copy className="w-4 h-4" /> {copied ? "Copy again" : "Copy to clipboard"}
        </button>
      </div>
    </div>
  );
};


const ReferencePhotosPanel = ({ photos }) => {
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const downloadOne = async (photo) => {
    setBusyId(photo.id);
    try {
      const r = await fetch(`${API}/reference-photos/${photo.id}/file?token=${encodeURIComponent(getToken() || "")}`);
      if (!r.ok) throw new Error("fetch failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.file_name || `reference-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Photo downloaded — now attach it in ChatGPT.");
    } catch {
      toast.error("Could not download photo");
    } finally {
      setBusyId(null);
    }
  };

  if (!photos || photos.length === 0) {
    return (
      <div className="mt-6 bsp-card p-5 border-l-4 border-[#D4A853] flex items-start gap-3" data-testid="no-ref-photos">
        <ImageIcon className="w-5 h-5 text-[#D4A853] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-[#1A1F3D]">No reference photos uploaded yet</div>
          <div className="text-sm text-[#52525b] mt-1">
            Head back to <Link to="/app/step1" className="gold-link">Step 1: Upload Reference Photos</Link> first — you'll need at least one to attach in ChatGPT.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 bsp-card p-5 md:p-6" data-testid="ref-photos-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="ref-photos-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F5E6E0] flex items-center justify-center shrink-0">
            <ImageIcon className="w-5 h-5 text-[#1A1F3D]" strokeWidth={1.6} />
          </div>
          <div>
            <span className="eyebrow">Your reference photos</span>
            <h2 className="font-display text-xl md:text-2xl text-[#1A1F3D] mt-0.5">
              Download one before heading to ChatGPT
            </h2>
          </div>
        </div>
        <span className="text-[#52525b] text-sm hidden sm:inline">{photos.length} uploaded</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#52525b]" /> : <ChevronDown className="w-5 h-5 text-[#52525b]" />}
      </button>

      {open && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="ref-photos-grid">
          {photos.map((p) => (
            <RefThumb key={p.id} photo={p} busy={busyId === p.id} onDownload={() => downloadOne(p)} />
          ))}
        </div>
      )}
    </div>
  );
};

const RefThumb = ({ photo, busy, onDownload }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/reference-photos/${photo.id}/file?token=${encodeURIComponent(getToken() || "")}`);
        if (!r.ok) return;
        const b = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      } catch (err) {
        console.error("Failed to load reference thumbnail:", err);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.id]);

  return (
    <div className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5E6E0]" data-testid={`ref-thumb-${photo.id}`}>
      {src ? (
        <img src={src} alt={photo.file_name || "Reference"} className="w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Star className="w-5 h-5 text-[#D4A853] animate-pulse" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" />
      <button
        type="button"
        onClick={onDownload}
        disabled={busy}
        className="absolute bottom-1.5 left-1.5 right-1.5 inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-md bg-[#D4A853] text-[#1A1F3D] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-[#C29543] disabled:opacity-100 disabled:cursor-wait"
        data-testid={`download-ref-${photo.id}`}
      >
        <Download className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Download"}
      </button>
    </div>
  );
};
